/**
 * Gallery component
 * 
 * @module gallery
 * @version 1.0.0
 * @extends HTMLElement
 */
class galleryComponent extends HTMLElement {

    /**
     * Product gallery web component.
     * 
     * @constructor
     */
    constructor() {

        super();

        /**
         * Gallery element.
         * 
         * @var {Object}
         */
        this.gallery = this.querySelector('[data-gallery]');

        /**
         * Thumbnails element.
         * 
         * @var {Object}
         */
        this.thumbnails = this.querySelector('[data-thumbnail-slider]');

        this.initCells();
        this.initSlider();
        this.initEvents();
        this.initPhotoSwipeEvents();

        if(window.screenMode == 'desktop-large' ) {
            this.scrollToMiddleOfFirstImage();
        }
    }

    /**
     * Init gallery cells array to handle image visibility on change variant.
     * 
     * @returns {undefined}
     */
    initCells() {

        /**
         * Gallery cells array
         * 
         * @var {Array}
         */
        this.cells = [];

        /**
         * Number of visible cells in the gallery based of the variant selected.
         * 
         * @var {Array}
         */
        this.activeCells = 0;

        this.gallery.querySelectorAll('[data-gallery-item]').forEach(item => {

            let index = item.dataset.galleryItemIndex;

            this.cells[index] = {
                index: parseInt(index),
                status: true,
                alt: item.dataset.galleryItem,
                element: item
            };
        });
    }

     /**
     * Scroll the window to the middle of the first image in the gallery.
     */
    scrollToMiddleOfFirstImage() {
        const firstImage = this.gallery.querySelector('.gallery__item img');
        if (firstImage) {
            const imageRect = firstImage.getBoundingClientRect();
            const scrollPosition = window.scrollY + imageRect.top + (imageRect.height / 2) - (window.innerHeight / 2);
            window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            });
        }
    }

    /**
	 * Init gallery slider.
     * 
     * @returns {undefined}
	 */
    initSlider() {

        this.querySelectorAll('.swiper-lazy-loading').forEach(element => {
            element.classList.remove('swiper-lazy-loading');
        });

        let disableSwiperDesktop = this.dataset.disableSwiperDesktop || "false";

        let defaultOptions = {
            enabled: true,
            preloadImages: true,
            lazy: {
                loadPrevNext: true,
            },
            autoHeight: true,
            pagination: {
                el: '.swiper-gallery-pagination',
                dynamicBullets: true
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
            },
            scrollbar: {
                el: '.swiper-scrollbar',
            },
            enabled: true,
            breakpoints: {
              600: {
                enabled: false
              }
            },
            // speed: 500,
            // mousewheel: {
            //     thresholdDelta: 5,
            // },
            on: {
                init: () => {

                    if (!this.thumbnails)
                        return false;

                    this.items = this.thumbnails.querySelectorAll('[data-thumbnail-item]');

                    // this.initThumbnails();

                    if (typeof window.theme.carousel != 'undefined')
                        window.theme.carousel.loadInfinityDots(this);
                },
                slideChange: index => {

                    const activeImage = index.slides[index.activeIndex]?.querySelector('img');

                    if(window.screenMode == 'mobile' && activeImage) {
                        this.getColorFromImage(activeImage.src).then(color => {
                            let rgb = color.match(/\d+/g).map(Number);
                            let yiq = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;
                            let isDarkColor = (parseInt(yiq) < 128) ? true : false;
                                    
                            if (isDarkColor) {
                                document.body.classList.add('light-text');
                            } else {
                                document.body.classList.remove('light-text');
                            }
                        });
                    }

                    if (!this.thumbnails)
                        return false;

                    this.activeThumbnail(index); 

                    if (typeof window.theme.carousel != 'undefined')
                        window.theme.carousel.loadInfinityDots(this);
                },
                resize: () => {
                    if((disableSwiperDesktop)&&(window.screenMode == 'desktop')){
                        this.slider.activeIndex = 0;
                    }
                }
            }
        }

        let options = (this.dataset.options) ? JSON.parse(this.dataset.options) : {};

        this.options = {...defaultOptions, ...options};
    
        this.slider = new Swiper(this.gallery, this.options);
    }

    /**
     * Init gallery events.
     * 
     * @returns {undefined}
     */
     initEvents() {

        // On variant change
        window.addEventListener('variant-change', event => {
            if( event.detail.product.id == this.dataset.galleryProductId){
                this.swapImages(event.detail.product, event.detail.variant);
            }
        });
    }

    getColorFromImage(imageUrl) {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'Anonymous';
            image.src = imageUrl;
            image.onload = () => {
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              const width = image.width;
              const height = Math.min(image.height, 90); 
              canvas.width = width;
              canvas.height = height;
              context.drawImage(image, 0, 0, width, height);
              const data = context.getImageData(0, 0, width, height).data;
              
              let r = 0, g = 0, b = 0;
              const pixelCount = width * height;
              for (let i = 0; i < data.length; i += 4) {
                r += data[i];
                g += data[i + 1];
                b += data[i + 2];
              }
              r = Math.round(r / pixelCount);
              g = Math.round(g / pixelCount);
              b = Math.round(b / pixelCount);
      
              resolve(`rgb(${r}, ${g}, ${b})`);
            };
            image.onerror = reject;
        });
    }

    /**
     * Init gallery thumbnails.
     * 
     * @returns {undefined}
     */
     initThumbnails() {

        //Mark the first element as active
        this.thumbnails.querySelector('[data-thumbnail-item]').classList.add("active");

        this.items.forEach(element => {

            element.addEventListener('click', event => {

                event.preventDefault();

                let index = element.dataset.thumbnailIndex;

                this.activeThumbnail(index);

                this.slider.slideTo(index, 500);

            }, false);
        });
	}

    /**
     * Active thumbnail by index.
     * 
     * @param {integer} index Thumbnail index.
     * @returns {undefined}
     */
	activeThumbnail(index) {

        // Remove all actived elements
        this.items.forEach((element) => {
            element.classList.remove("active");
        });

        let thumbnail = this.thumbnails.querySelector("[data-thumbnail-index='" + index + "']");

        if (thumbnail) {
            thumbnail.classList.add("active");
        }
	}

    /**
     * Swap gallery images by variant.
     * 
     * @param {object} product Product data.
     * @param {object} variant Variant data.
     * @returns {undefined}
     */
     swapImages(product, variant) {

        let alt = this.getCurrentAlt(product, variant);

        this.destroySlider();

        if (alt) {

            this.activeCells = 0;

            this.cells.forEach(cell => {

                if (cell.alt && cell.alt.toLowerCase() == alt) {

                    this.enableImage(cell.index);
                    this.activeCells++;
                    
                } else {

                    this.disableImage(cell.index);
                }
            });

            this.updateThumbnailsVisibility(alt);

        } else {

            this.showAllImages();
        }

        this.initSlider();
    }

    /**
     * Get image alt from current variant active.
     * 
     * @param {object} product Product data.
     * @param {object} variant Variant data.
     * @returns {string} Image alt.
     */
     getCurrentAlt(product, variant) {

        let currentAlt = null;

        if (product.media) {
        
            product.media.forEach(image => {

                if (image.alt && variant) {

                    let alt       = image.alt.toLowerCase(),
                        altArray  = alt.split('/'),
                        reference = (altArray[1]) ? altArray[1].trim() : null;

                    if (variant.title.toLowerCase().includes(reference) && !alt.includes('swatch')) {
                        currentAlt = alt;
                    }
                }
            });

            return currentAlt;

        } 
    }

    /**
     * Destroy gallery slider.
     * 
     * @returns {undefined}
     */
    destroySlider() {

        this.slider.destroy();
    }

    /**
     * Disable image by gallery cell index.
     * 
     * @param {integer} index Gallery cell index.
     * @returns {undefined}
     */
    disableImage(index) {
        
        this.cells[index].status = false;

        let element = this.querySelector('[data-gallery-item-index="'+ index +'"]');

        if (element) {
            element.remove();
        }
    }

    /**
     * Enable image by gallery cell index.
     * 
     * @param {integer} index Gallery cell index.
     * @returns {undefined}
     */
    enableImage(index) {

        this.cells[index].status = true;

        if (!this.querySelector('[data-gallery-item-index="' + index + '"]')) {

            this.gallery.querySelector('[data-gallery-wrapper]').append(this.cells[index].element);
        }
    }

    /**
     * Enable thumbnail element.
     * 
     * @param {object} thumbnail Thumbnail element.
     * @param {integer} index Thumbnail index.
     * @returns {undefined}
     */
    enableThumbnail(thumbnail, index) {
        thumbnail.style.display = 'block';
        thumbnail.dataset.thumbnailIndex = index;
    }

    /**
     * Disable thumbnail element.
     * 
     * @param {object} thumbnail Thumbnail element.
     * @returns {undefined}
     */
    disableThumbnail(thumbnail) {
        thumbnail.dataset.thumbnailIndex = -1;
        thumbnail.style.display = 'none';
    }

    /**
     * Active thumbnail by a custom click event.
     * 
     * @param {object} thumbnail Thumbnail element.
     * @returns {undefined}
     */
    triggerThumbnailClick(thumbnail) {
        let event = new CustomEvent('click', {});
        thumbnail.dispatchEvent(event);
        return true;
    }

    /**
     * Show all galerry images.
     * 
     * @param {object} thumbnail Thumbnail element.
     * @returns {undefined}
     */
    showAllImages() {

        // Disable all images
        this.cells.forEach((cell, index) => {
            this.disableImage(index);
        });

        // Display all images except swatches
        this.cells.forEach((cell, index) => {
            if (cell.alt && cell.alt.toLowerCase().includes('swatch')) {
                this.disableImage(index);
            } else {
                this.enableImage(index);
            }
        });

        // Display all thumbnails except swatches
        this.updateThumbnailsVisibility();
    }

    /**
     * Update thumbnails visibility by image alt.
     * 
     * @param {string} alt Image alt.
     * @returns {undefined}
     */
     updateThumbnailsVisibility(alt) {
        
        let activeThumbnail = false, index = 0;

        document.querySelectorAll('[data-thumbnail-item]').forEach(thumbnail => {

            if (thumbnail.dataset.thumbnailItem) {

                // Display thumbnails via alt
                if (alt) {

                    if (thumbnail.dataset.thumbnailItem.toLowerCase() == alt.toLowerCase()) {

                        this.enableThumbnail(thumbnail, index);
    
                        index++;
        
                        if (!activeThumbnail)
                            activeThumbnail = this.triggerThumbnailClick(thumbnail);

                    } else {

                        this.disableThumbnail(thumbnail);

                    }

                } else {

                    // Display all thumbnails except swatches
                    if (!thumbnail.dataset.thumbnailItem.toLowerCase().includes('swatch')) {
        
                        this.enableThumbnail(thumbnail, index);
            
                        index++;
    
                        if (!activeThumbnail)
                            activeThumbnail = this.triggerThumbnailClick(thumbnail);
                    }
                }

            } else {

                this.disableThumbnail(thumbnail);
            }
        });
    }

    /**
     * Init PhotoSwipe events.
     * 
     * @returns {undefined}
     */
     initPhotoSwipeEvents() {
        // Init zoom image
        let zoomImage = document.querySelectorAll('[data-pswp-img]');

        this.initZoomImage(zoomImage);

        // On screen change
        window.addEventListener('screen-breakpoint', event => {
            this.initZoomImage(zoomImage);
        });
    }
    
    
    /**
    * Start zoom with PhotoSwipe gallery on product image
    * 
    * @param {object} zoomImage Images that will init zoom.
    * @returns {undefined}
    */

     initZoomImage(zoomImage) {
        if(window.screenMode == 'tablet' || window.screenMode == 'desktop'  || window.screenMode == 'desktop-large' ) {
            //active on tablet, desktop and desktop-large devices by clicking the zoom button
            let zoomBtn = document.querySelector('[data-zoom]');

            if(zoomBtn && !zoomBtn.classList.contains('zoom-active') ){
                zoomBtn.classList.add('zoom-active');

                //init the zoom PhotoSwipe starting with the selected image
                zoomBtn.addEventListener('click', (event) => {
                    let image = document.querySelectorAll('[data-thumbnail-item]');

                    let indexImg;
                    image.forEach((element, index) => {
                        if (element.classList.contains('active')) {
                            indexImg = index;
                        }
                    });

                    this.initPhotoSwipe(zoomImage, indexImg);
                });
            }
        } else {

            // active on mobile by clicking the image
            zoomImage.forEach((element, index) => {
                if (!element.classList.contains('zoom-active')) {
                    element.classList.add('zoom-active');

                    //init the zoom PhotoSwipe starting with the selected image
                    element.addEventListener('click', (event) => {
                        this.initPhotoSwipe(zoomImage, index);
                    });
                }
            });
        }
    }

    /**
     * Init the PhotoSwipe
     * 
     * @param {object} zoomImage Images that will init PhotoSwipe.
     * @param {integer} index Image index where the PhotoSwipe will start.
     * @returns {undefined}
     */

    initPhotoSwipe(zoomImage, index) {
        let items = [];

        zoomImage.forEach((element) => {
            let img = { 
                src: element.dataset.pswpSrc,
                w: element.dataset.pswpWidth,
                h: element.dataset.pswpHeight
            };
            items.push(img);
        });

        let pswpElement = document.querySelector('.pswp');

        let options = {
            tapToClose: true,
            fullscreenEl: false,
            shareEl: false,
            index: index
        };
        if (typeof PhotoSwipe !== 'undefined') {
            let gallery = new PhotoSwipe(pswpElement, window.PhotoSwipeUI_Default, items, options);
            gallery.init();
        } else {
            console.error('PhotoSwipe is not defined. Please ensure PhotoSwipe library is loaded.');
        }
        // let gallery = new PhotoSwipe( pswpElement, window.PhotoSwipeUI_Default, items, options);
        // gallery.init();
    }
}

customElements.define('component-gallery', galleryComponent);
